import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Download, ExternalLink, RefreshCw, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const map = {
    completed: 'bg-green-100 text-green-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
  };
  const cls = map[status] || 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${cls}`}>
      {status}
    </span>
  );
};

const ReportCell = ({ session, onRegenerate, busy }) => {
  const s = session.report_status || 'not_started';

  if (s === 'completed' && session.report_url) {
    return (
      <div className="flex items-center gap-2">
        <a
          href={session.report_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline flex items-center gap-1"
          data-testid={`report-link-${session.id}`}
        >
          <FileSpreadsheet className="w-3.5 h-3.5" />
          Download
        </a>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRegenerate(session.id)}
          disabled={busy}
          data-testid={`regenerate-report-${session.id}`}
          title="Regenerate report"
          className="h-7 px-2"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
        </Button>
      </div>
    );
  }

  if (s === 'generating') {
    return (
      <span className="text-xs text-amber-600 flex items-center gap-1">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        Generating report…
      </span>
    );
  }

  if (s === 'failed') {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-red-600" title={session.report_error || ''}>
          Failed
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRegenerate(session.id)}
          disabled={busy}
          className="h-7 px-2"
          data-testid={`retry-report-${session.id}`}
        >
          Retry
        </Button>
      </div>
    );
  }

  // not_started or null
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => onRegenerate(session.id)}
      disabled={busy}
      data-testid={`generate-report-${session.id}`}
    >
      Generate
    </Button>
  );
};

export const ExportsTab = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyMap, setBusyMap] = useState({});

  useEffect(() => {
    fetchSessions();
    const id = setInterval(fetchSessions, 8000); // light auto-refresh for status
    return () => clearInterval(id);
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await api.get('/admin/sessions');
      setSessions(response.data);
    } catch (error) {
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const regenerateReport = async (sessionId) => {
    setBusyMap((m) => ({ ...m, [sessionId]: true }));
    try {
      await api.post(`/admin/sessions/${sessionId}/generate-report`);
      toast.success('Report generation triggered. It will appear shortly.');
      fetchSessions();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to start report generation');
    } finally {
      setBusyMap((m) => ({ ...m, [sessionId]: false }));
    }
  };

  // Group sessions by interview_name (fallback: interview_id)
  const grouped = sessions.reduce((acc, s) => {
    const key = s.interview_name || s.interview_id || 'Unknown Interview';
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  const exportInterviewCSV = (interviewName, rows) => {
    if (!rows.length) {
      toast.error('No data to export');
      return;
    }
    const headers = [
      'Candidate Name', 'Candidate Email', 'Status',
      'Started At', 'Completed At', 'Video URL', 'AI Report URL',
    ];
    const csvRows = rows.map((r) => [
      r.candidate_name,
      r.candidate_email,
      r.status,
      formatDate(r.started_at),
      r.completed_at ? formatDate(r.completed_at) : 'N/A',
      r.video_url || 'N/A',
      r.report_url || 'N/A',
    ]);
    const csv = [
      headers.join(','),
      ...csvRows.map((row) => row.map((c) => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${interviewName.replace(/[^a-z0-9]/gi, '_')}-sessions-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-medium">Interview Exports</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchSessions}
          data-testid="refresh-exports"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : Object.keys(grouped).length === 0 ? (
        <p className="text-center text-slate-500 py-12">No interview sessions yet</p>
      ) : (
        <Accordion type="single" collapsible>
          {Object.entries(grouped).map(([interviewName, rows]) => (
            <AccordionItem key={interviewName} value={interviewName}>
              <div className="flex items-center gap-4 py-2">
                <AccordionTrigger
                  className="flex-1"
                  data-testid={`export-interview-row-${interviewName}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="font-medium">{interviewName}</span>
                    <span className="text-xs text-slate-500">
                      {rows.length} session{rows.length === 1 ? '' : 's'}
                    </span>
                  </div>
                </AccordionTrigger>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    exportInterviewCSV(interviewName, rows);
                  }}
                  data-testid={`export-csv-${interviewName}`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSV
                </Button>
              </div>

              <AccordionContent>
                <div className="pl-2 pr-1 pb-2">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Candidate</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Completed</TableHead>
                        <TableHead>Video</TableHead>
                        <TableHead>AI Report</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((s) => (
                        <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                          <TableCell className="font-medium">{s.candidate_name}</TableCell>
                          <TableCell>{s.candidate_email}</TableCell>
                          <TableCell>
                            <StatusBadge status={s.status} />
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {formatDate(s.started_at)}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {s.completed_at ? formatDate(s.completed_at) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {s.video_url ? (
                              <a
                                href={s.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1"
                              >
                                View <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className="text-slate-400">N/A</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <ReportCell
                              session={s}
                              onRegenerate={regenerateReport}
                              busy={!!busyMap[s.id]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};
